import { useCallback, useRef } from 'react'

import { useLazyQuery } from '@apollo/client'

import { GET_COMPANY_TAGS } from 'src/graphql/companyTags.graphql'
import { GET_EMPLOYEES } from 'src/graphql/employees.graphql'
import { GET_JOB_LISTINGS } from 'src/graphql/joblistings.graphql'

import {
  RequiredDataTypes,
  getMissingDataTypes,
} from '../utils/dataLoadingUtils'

interface UseConditionalDataLoadingProps {
  onError: () => void
}

export const useConditionalDataLoading = ({
  onError,
}: UseConditionalDataLoadingProps) => {
  // Saved-filter hydration loads enough job listings to resolve labels for
  // selected IDs at page load. Bumped from 100 to give customers with large
  // catalogs (200+ listings) full label resolution. Proper long-term fix is
  // an "in IDs" filter on the backend so we can query just the listings we
  // need rather than scanning a fixed top-N — out of scope for ENG-4248.
  const [loadJobListings] = useLazyQuery(GET_JOB_LISTINGS, {
    variables: {
      pageInfo: { offset: 0, limit: 500 },
    },
    onError,
  })

  const [loadRecruiters] = useLazyQuery(GET_EMPLOYEES, {
    variables: {
      filters: { roleTypes: ['RECRUITER'] },
      pageInfo: { offset: 0, limit: 100 },
    },
    onError,
  })

  const [loadTags] = useLazyQuery(GET_COMPANY_TAGS, {
    variables: {
      pageInfo: { offset: 0, limit: 100 },
    },
    onError,
  })

  // Store loaded options for merging with presets
  const loadedOptionsRef = useRef({
    jobListings: [],
    recruiters: [],
    tags: [],
  })

  const loadRequiredData = useCallback(
    async (requiredDataTypes: RequiredDataTypes) => {
      const missingDataTypes = getMissingDataTypes(
        requiredDataTypes,
        loadedOptionsRef.current
      )

      if (missingDataTypes.length === 0) {
        return Promise.resolve()
      }

      const loadPromises = []

      if (missingDataTypes.includes('jobListings')) {
        loadPromises.push(loadJobListings())
      }

      if (missingDataTypes.includes('recruiters')) {
        loadPromises.push(loadRecruiters())
      }

      if (missingDataTypes.includes('tags')) {
        loadPromises.push(loadTags())
      }

      try {
        const results = await Promise.all(loadPromises)

        // Update the loaded options ref with the new data
        results.forEach((result, index) => {
          const dataType = missingDataTypes[index]
          if (result.data) {
            switch (dataType) {
              case 'jobListings':
                loadedOptionsRef.current.jobListings =
                  result.data.jobListings?.items ?? []
                break
              case 'recruiters':
                loadedOptionsRef.current.recruiters =
                  result.data.employees?.items ?? []
                break
              case 'tags':
                loadedOptionsRef.current.tags =
                  result.data.companyTags?.items ?? []
                break
            }
          }
        })

        return results
      } catch (error) {
        console.error('Error loading required data:', error)
        throw error
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [] // Remove dependencies to prevent infinite loops
  )

  const loadAllData = useCallback(async () => {
    const allRequired: RequiredDataTypes = {
      jobListings: true,
      recruiters: true,
      tags: true,
    }

    return loadRequiredData(allRequired)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Remove loadRequiredData dependency to prevent infinite loops

  return {
    loadRequiredData,
    loadAllData,
    loadedOptionsRef,
  }
}
